import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import Icon from './Icon';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { installTimerBridge, workedMsOf, parseServerTime } from './timerBus';
import api, { isPortalHost, setPortalLinkBase, getShared } from './api';
import { RecordConversationModal } from './ClientConversations';
import { BrandMark } from './Logo';
import { MeContext, can } from './Me';
import Gated from './Gated';
import Tour from './Tour';
import { MeetingProvider, MeetingGuard, MeetingBar, useMeeting } from './MeetingMode';
import ErrorBoundary, { isStaleBuild } from './ErrorBoundary';
import { ConfirmHost } from './Confirm';
import { RecorderProvider } from './HowTo';
import AuthGate from './Auth';
import './index.css';
import './App.css';
import WhatsNew, { openWhatsNew } from './WhatsNew';

// A page's code, loaded the first time it is opened — and an update shipped in the meantime never
// blanks the screen. After a deploy the old build's chunks are gone, so the first page somebody opens
// that they had not opened yet fails to load. It is tried once more (the server may just be
// restarting); if it still cannot load, that page alone says it has been updated, the rest of the app
// keeps working, and the "New version" bar offers the refresh — instead of the whole tree being
// replaced by a refresh prompt.
function UpdatedSection() {
  return (
    <div className="stale-section" role="status">
      This page has been updated since you opened the Hub.{' '}
      <button type="button" className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Refresh to open it</button>
    </div>
  );
}
const lazyPage = (load) => React.lazy(() => load().catch(async (e) => {
  if (!isStaleBuild(e)) throw e;
  await new Promise(r => setTimeout(r, 1500));
  try { return await load(); } catch (again) {
    window.dispatchEvent(new Event('pm-new-version'));
    return { default: UpdatedSection };
  }
}));

// Pages, each loaded the first time it is opened (its own chunk). The client portal used to pull
// down the whole staff app — every page of it, ~2.5 MB — before it could draw a login box; now it
// loads the portal pages and nothing else.
const Templates = lazyPage(() => import('./pages/Templates'));
const Performance = lazyPage(() => import('./pages/Performance'));
const ActivityStatements = lazyPage(() => import('./pages/ActivityStatements'));
const Shared = lazyPage(() => import('./pages/Shared'));
const Sales = lazyPage(() => import('./pages/Sales'));
const Work = lazyPage(() => import('./pages/Work'));
const Planner = lazyPage(() => import('./pages/Planner'));
const Triage = lazyPage(() => import('./pages/Triage'));
const Recordings = lazyPage(() => import('./pages/Recordings'));
const IntakeForm = lazyPage(() => import('./pages/IntakeForm'));
const IntakeForms = lazyPage(() => import('./pages/IntakeForms'));
const Payroll = lazyPage(() => import('./pages/Payroll'));
const Assistant = lazyPage(() => import('./pages/Assistant'));
const MyTasks = lazyPage(() => import('./pages/MyTasks'));
const Clients = lazyPage(() => import('./pages/Clients'));
const ClientDetail = lazyPage(() => import('./pages/ClientDetail'));
const Requests = lazyPage(() => import('./pages/Requests'));
const Reconciliation = lazyPage(() => import('./pages/Reconciliation'));
const TimePage = lazyPage(() => import('./pages/Time'));
const Staff = lazyPage(() => import('./pages/Staff'));
const Settings = lazyPage(() => import('./pages/Settings'));
const PricingAdmin = lazyPage(() => import('./pages/PricingAdmin'));
const Import = lazyPage(() => import('./pages/Import'));
const ClientPortal = lazyPage(() => import('./pages/ClientPortal'));
const ClientPortalAll = lazyPage(() => import('./pages/ClientPortalAll'));
const SetPassword = lazyPage(() => import('./pages/SetPassword'));
const MagicLink = lazyPage(() => import('./pages/MagicLink'));
const EmailLink = lazyPage(() => import('./pages/EmailLink'));
const PreviewEntry = lazyPage(() => import('./pages/PreviewEntry'));
const BookingPage = lazyPage(() => import('./pages/BookingPage'));
const Pricing = lazyPage(() => import('./pages/Pricing'));
const Quotes = lazyPage(() => import('./pages/Quotes'));
const QuoteView = lazyPage(() => import('./pages/QuoteView'));
const Estimator = lazyPage(() => import('./pages/Estimator'));
const Feedback = lazyPage(() => import('./pages/Feedback'));
const StaffSetPassword = lazyPage(() => import('./pages/StaffSetPassword'));
const EstimatorAdmin = lazyPage(() => import('./pages/EstimatorAdmin'));

// What shows while a page's code arrives. Small and calm: usually it is there in a blink.
function PageLoading({ portal = false }) {
  const inner = <div className="page-loading" role="status" aria-live="polite"><span className="page-loading-spinner" aria-hidden="true" /> Loading…</div>;
  return portal ? <div className="client-portal"><div className="client-card">{inner}</div></div> : inner;
}

const IDLE_LIMIT_MS = 30 * 60 * 1000; // prompt after 30 min with no activity
// `parseServerTime` used to be declared here as well as in `timerBus`, identically but for the
// empty-string guard. Two copies of the reader that decides what a server stamp means is the same
// shape as the finding this file was just edited for — one arithmetic, in one place, read by both
// the banner and the thing that banks the time.
const fmtClock = (totalSec) => {
  const sec = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${h > 0 ? h + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Sticky bar shown across the app whenever the current user has a timer running.
// Also watches for idle: if there's been no activity for 30 min it asks whether
// you were working, so a forgotten timer never silently logs hours you didn't work.
// A full-screen prompt when a scheduled meeting or a pinned-start workflow is due to begin.
// "Start" begins the timer for a workflow (or opens the meeting); "Defer" snoozes 10 minutes.
const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Dismissed prompts persist in the browser (auto-expiring) so a cancelled meeting stays gone after
// a page reload, instead of the reminder popping back.
const DISMISS_KEY = 'pm-dismissed-prompts';
const loadDismissed = () => {
  try {
    const o = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
    const now = Date.now(); let changed = false;
    for (const k of Object.keys(o)) { if (!(o[k] > now)) { delete o[k]; changed = true; } }
    if (changed) localStorage.setItem(DISMISS_KEY, JSON.stringify(o));
    return o;
  } catch { return {}; }
};
const persistDismiss = (key) => {
  try { const o = loadDismissed(); o[key] = Date.now() + 18 * 3600 * 1000; localStorage.setItem(DISMISS_KEY, JSON.stringify(o)); } catch { /* storage unavailable */ }
};

function StartPrompt() {
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [timer, setTimer] = useState(null); // the currently-running timer, so we can offer "continue current"
  const itemsRef = useRef([]);
  const startedRef = useRef(new Set());          // meetings whose clock has already been started
  const [started, setStarted] = useState(null);  // what starting it did: the client, the notetaker
  const [recording, setRecording] = useState(null); // the client a recording was asked for
  const handledRef = useRef(new Set());
  const snoozeRef = useRef({});
  // Seed already-dismissed prompts from the last session so they don't reappear.
  useEffect(() => { Object.keys(loadDismissed()).forEach(k => handledRef.current.add(k)); }, []);

  const loadSchedule = useCallback(async () => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const list = [];
    try {
      const r = await api.get(`/gcal/events?from=${start.toISOString()}&to=${end.toISOString()}`);
      (r.data.events || []).forEach(e => {
        if (e.all_day || e.personal || !e.start) return;
        const s = new Date(e.start); if (isNaN(s)) return;
        list.push({ key: `m:${e.id}`, kind: 'meeting', id: e.id, title: e.summary, startMs: s.getTime(), html_link: e.html_link, prep: e.prep_minutes || 0 });
      });
    } catch { /* ignore */ }
    try {
      // The Work page usually loaded this a moment ago; its answer will do for a schedule check.
      const w = await getShared('/work', { maxAgeMs: 15000 });
      const tstr = ymdLocal(new Date());
      (w.data || []).forEach(x => {
        if (x.archived || x.status === 'complete') return;
        if (x.plan_date === tstr && x.plan_start && /^\d{2}:\d{2}$/.test(x.plan_start)) {
          const [hh, mm] = x.plan_start.split(':').map(Number);
          const d = new Date(); d.setHours(hh, mm, 0, 0);
          list.push({ key: `w:${x.id}:${x.plan_start}`, kind: 'wf', id: x.id, title: x.title, startMs: d.getTime() });
        }
      });
    } catch { /* ignore */ }
    itemsRef.current = list;
    try { const t = await api.get('/timer/current'); setTimer(t.data && t.data.running ? t.data : null); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadSchedule();
    const iv = setInterval(loadSchedule, 5 * 60 * 1000);
    window.addEventListener('pm-timer-changed', loadSchedule);
    return () => { clearInterval(iv); window.removeEventListener('pm-timer-changed', loadSchedule); };
  }, [loadSchedule]);

  // The clock, started the moment a meeting's prompt appears. It is the thing most often
  // forgotten and the least risky to do automatically — an hour with a client is the most
  // expensive hour in the week — and it is one click to stop if the meeting is not happening.
  useEffect(() => {
    if (!item || item.kind !== 'meeting' || startedRef.current.has(item.key)) return;
    startedRef.current.add(item.key);
    (async () => {
      try {
        const r = await api.post(`/gcal/events/${item.id}/start`, { title: item.title, start: new Date(item.startMs).toISOString() });
        setStarted(r.data);
        if (r.data.started) window.dispatchEvent(new Event('pm-timer-changed'));
      } catch { setStarted(null); }
    })();
  }, [item]);

  useEffect(() => {
    const check = () => {
      if (item) return;
      const now = Date.now();
      for (const it of itemsRef.current) {
        if (handledRef.current.has(it.key)) continue;
        const snooze = snoozeRef.current[it.key] || 0;
        if (now < Math.max(it.startMs, snooze)) continue;            // not yet time
        if (!snooze && now - it.startMs > 30 * 60 * 1000) { handledRef.current.add(it.key); continue; } // too late, skip
        setItem(it); break;
      }
    };
    check();
    const iv = setInterval(check, 20000);
    return () => clearInterval(iv);
  }, [item]);

  if (!item) return null;
  const when = new Date(item.startMs).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  // A transition: the next scheduled task is due while a DIFFERENT workflow's timer is still running.
  const running = timer && timer.running ? timer : null;
  const isTransition = item.kind === 'wf' && running && running.work_item_id !== item.id;
  const dismiss = () => { handledRef.current.add(item.key); persistDismiss(item.key); setItem(null); };
  const startTimer = async (id) => { try { await api.post(`/work/${id}/timer/start`); window.dispatchEvent(new Event('pm-timer-changed')); } catch { /* ignore */ } };
  const start = async () => {
    if (item.kind === 'wf') {
      await startTimer(item.id);
      handledRef.current.add(item.key); setItem(null); navigate(`/?focus=${item.id}`);
    } else {
      if (item.html_link) window.open(item.html_link, '_blank', 'noopener');
      dismiss();
    }
  };
  const defer = () => { snoozeRef.current[item.key] = Date.now() + 10 * 60 * 1000; setItem(null); };

  return createPortal(
    <div className="start-prompt-overlay">
      <div className="start-prompt">
        <div className="start-prompt-icon">{isTransition ? '⏭️' : item.kind === 'meeting' ? <Icon name="calendar" /> : '⏱'}</div>
        <div className="start-prompt-kicker">{isTransition ? 'Next task' : item.kind === 'meeting' ? 'Meeting starting' : 'Time to start'} · {when}</div>
        <div className="start-prompt-name">{item.title}</div>
        <div className="start-prompt-sub">
          {isTransition ? <>You’re currently timing <strong>{running.title}</strong>. Move on, or keep going?</>
            : item.kind === 'meeting' ? (started
              ? (started.started
                ? `The clock is running for ${started.client_name}${started.stopped_previous ? ' — what you were timing before was stopped and kept' : ''}.`
                : started.reason)
              : 'Your meeting is starting now.')
              : 'This is scheduled to start now — start the timer and get going.'}
        </div>
        <div className="start-prompt-actions">
          {isTransition ? (
            <>
              <button className="btn btn-ghost" onClick={defer}>↩ Continue current task</button>
              <button className="btn btn-primary start-prompt-go" onClick={start}>▶ Start next task</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={defer}>Defer 10 min</button>
              <button className="btn btn-ghost" onClick={dismiss} title={item.kind === 'meeting' ? 'Dismiss — e.g. the meeting was cancelled' : 'Dismiss this reminder'}>Dismiss</button>
              {item.kind === 'meeting' && started && started.started && (
                started.notetaker && started.notetaker.armed
                  ? <span className="start-prompt-note"><Icon name="mic" /> The notetaker is joining this one.</span>
                  : <button className="btn btn-ghost" onClick={() => setRecording(started)}><Icon name="mic" /> Record it</button>
              )}
              <button className="btn btn-primary start-prompt-go" onClick={start}>{item.kind === 'meeting' ? 'Open meeting →' : '▶ Start'}</button>
            </>
          )}
        </div>
      </div>
      {recording && (
        <RecordConversationModal
          clientId={recording.client_id}
          clientName={recording.client_name}
          defaultType="online"
          workItemId={recording.work_item_id}
          workTaskId={recording.task_id}
          autoStart
          onClose={() => setRecording(null)}
          onCreated={() => setRecording(null)}
        />
      )}
    </div>,
    document.body
  );
}

// How long "not now" quietens the bar for. Long enough to get through a phone call or a coffee,
// short enough that a whole afternoon doesn't go unrecorded.
// "Not now" is for the rest of the day: until local midnight.
const snoozeUntilTomorrow = () => { const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime(); };
const SNOOZE_KEY = 'pm_timer_snooze_until';

// Slots that have already started themselves, so one starts at most once.
//
// Without this, stopping an auto-started timer would start it again a moment later — the slot is
// still "now", the conditions still hold, and the app would be arguing with somebody who has just
// told it no. It is keyed on the slot and not the workflow: the same job planned again at three
// o'clock is a second slot and gets its own go.
//
// Kept in localStorage rather than in state, because a reload is exactly when this would otherwise
// forget and start again. Only today's are kept.
const AUTO_KEY = 'pm_timer_autostarted';
const slotId = (s) => (s ? `${s.work_item_id}|${s.plan_start || ''}` : '');
const autoStartedToday = () => {
  try {
    const v = JSON.parse(localStorage.getItem(AUTO_KEY) || '{}');
    return v.day === new Date().toLocaleDateString('en-CA') ? (v.slots || []) : [];
  } catch { return []; }
};
const rememberAutoStart = (id) => {
  try {
    localStorage.setItem(AUTO_KEY, JSON.stringify({
      day: new Date().toLocaleDateString('en-CA'),
      slots: [...new Set([...autoStartedToday(), id])],
    }));
  } catch { /* a private window simply gets asked again */ }
};

// Work that is a page rather than a job.
//
// Going through the practice inbox is real time against no client and no workflow, so there was
// nothing to start a timer on — which is why these exist as ordinary workflows with no client.
// The page somebody is standing on is the best evidence there is of what they are about to do,
// and the picker was ignoring it: opened from Triage, it offered everything except Triage.
const PAGE_WORK = { '/triage': { key: 'triage', label: 'Triage' } };
const pageWorkFor = (pathname) => PAGE_WORK[String(pathname || '').replace(/\/+$/, '') || '/'] || null;

// The next pay day, said where it cannot be missed
//
// Payroll is the one thing here that has to happen on the day: a run posted the morning after
// is a client's staff not paid. It was said in exactly one place — a Triage notification — and
// Triage is a page somebody has to think to open, which on the day it matters they usually do
// not, because they are looking at something else.
//
// Only when it is nearly time. A banner that is always there is furniture within a week and
// gets read as part of the border, which is the same as not being there at all. Three days
// ahead, and for a week after a pay day that came and went unposted — the missed one is the
// case this exists for and it should not disappear the morning it becomes true.
//
// There is no dismiss on it. Ticking the run as posted is what makes it go, which is the thing
// it is asking for; a button that quiets it without doing that is a button for making the
// problem invisible.
const holds = (me, key) => !!me && (!!me.owner || !!(me.perms && me.perms[key]));

function PayrollDueBar() {
  const [next, setNext] = useState(null);
  const navigate = useNavigate();
  // Not asked until it is known this person has payroll: mounted while /api/me is still loading,
  // it used to ask anyway and log a 403 for everybody without it.
  const me = useContext(MeContext);
  const allowed = holds(me, 'can_view_payroll');
  useEffect(() => {
    if (!allowed) return undefined;
    let dead = false;
    const read = async () => {
      try { const r = await api.get('/payroll/next'); if (!dead) setNext(r.data); }
      catch { if (!dead) setNext(null); }
    };
    read();
    // Slowly. A pay date does not move, and the one thing that changes under it — somebody
    // ticking the run as posted — happens on a page away from here and can wait a quarter of
    // an hour to be noticed.
    const poll = setInterval(read, 15 * 60 * 1000);
    return () => { dead = true; clearInterval(poll); };
  }, [allowed]);

  const run = next && next.run;
  if (!run) return null;
  const d = run.days;
  const when = d === 0 ? 'today'
    : d === 1 ? 'tomorrow'
      : d > 1 ? `in ${d} days`
        : d === -1 ? 'yesterday'
          : `${-d} days ago`;
  const who = [run.client_name, run.cycle_name].filter(Boolean).join(' · ');
  return (
    <button type="button" className={`payday-bar${d <= 0 ? ' now' : ''}`}
      onClick={() => navigate('/payroll')}
      title={d < 0 ? 'Pay day has passed and this run is still not posted — open the payroll board' : 'Open the payroll board'}>
      <Icon name="alert" />
      <span className="payday-when">Pay day {when}</span>
      {who && <span className="payday-who">{who}</span>}
      <span className="payday-go">{d < 0 ? 'still not posted' : 'not posted yet'}</span>
    </button>
  );
}

function TimerBar() {
  // Whether this person wants to be spoken to at all. While it is still loading nothing is said —
  // a nudge that flashes up and disappears is worse than one that waits half a second.
  const me = useContext(MeContext);
  const prefs = (me && me.prefs) || null;
  const navigate = useNavigate();
  const [timer, setTimer] = useState(null);   // { title, started_at, lastActiveMs }
  // What to offer when nothing is running, and whether now is a time to be asking.
  const [idle, setIdle] = useState(null);     // { in_work_hours, suggestions, work_hours }
  const [picking, setPicking] = useState(false);
  // The workflow for the page being looked at, if it is one of the few that are timeable in
  // their own right. Fetched rather than assumed: it is made the first time it is asked for.
  const { pathname } = useLocation();
  const here = pageWorkFor(pathname);
  const [hereWork, setHereWork] = useState(null);
  const [snoozedUntil, setSnoozedUntil] = useState(() => Number(localStorage.getItem(SNOOZE_KEY) || 0));
  // The slot that started itself, until somebody has seen that it did.
  const [autoStarted, setAutoStarted] = useState(null);
  const [, setTick] = useState(0);
  const [prompting, setPrompting] = useState(false);
  const lastActivityRef = useRef(Date.now());  // last real user activity in this tab
  // A screen can ask for quiet while something needs the whole of somebody's attention (the Uncoded
  // upload preview): 'pm-timer-quiet' with { detail: { on } }. Counted, so two asking is still quiet
  // until both have finished.
  const [quietAsks, setQuietAsks] = useState(0);
  useEffect(() => {
    const on = (e) => setQuietAsks(n => Math.max(0, n + (e && e.detail && e.detail.on ? 1 : -1)));
    window.addEventListener('pm-timer-quiet', on);
    return () => window.removeEventListener('pm-timer-quiet', on);
  }, []);

  const fetchTimer = async () => {
    try {
      const r = await api.get('/timer/current');
      if (!r.data.running) { setTimer(null); return; }
      // Falling back to now rather than to zero where there is no stamp. The server sends
      // `last_active_at || started_at` and both are always set, so this cannot happen — but
      // `parseServerTime` answers 0 for nothing, and 0 here reads as idle since 1970 and fires the
      // “are you still there” prompt the moment the page loads.
      setTimer({ ...r.data, lastActiveMs: parseServerTime(r.data.last_active_at) || Date.now() });
    } catch { /* ignore */ }
  };

  // What the bar should offer while nothing is running. Only asked for when it is needed, so a
  // running timer costs nothing extra.
  const fetchIdle = useCallback(async () => {
    try { const r = await api.get('/timer/suggestion'); setIdle(r.data); }
    catch { setIdle(null); }
  }, []);

  // Other tabs on this machine hear about a change the moment it happens; another device has
  // only the poll, so the poll is closer together while something is running. A stale answer
  // matters in exactly one direction: a timer that has been paused elsewhere and is still being
  // drawn as counting here.
  useEffect(() => installTimerBridge(), []);
  useEffect(() => {
    fetchTimer();
    const onChange = () => fetchTimer();
    window.addEventListener('pm-timer-changed', onChange);
    const poll = setInterval(fetchTimer, timer ? 10000 : 30000);
    return () => { window.removeEventListener('pm-timer-changed', onChange); clearInterval(poll); };
  }, [!!timer]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the offer whenever the timer stops, and every few minutes while it is off — the
  // Planner moves on even when you don't.
  useEffect(() => {
    if (timer) { setPicking(false); return undefined; }
    fetchIdle();
    const t = setInterval(fetchIdle, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [timer, fetchIdle]);

  // Start one, from anywhere in the app.
  useEffect(() => {
    let alive = true;
    if (!here) { setHereWork(null); return undefined; }
    api.get(`/internal-work/${here.key}`)
      .then(r => { if (alive) setHereWork(r.data); })
      .catch(() => { if (alive) setHereWork(null); });  // the bar simply will not offer it
    return () => { alive = false; };
  }, [here]);

  const startOn = async (workItemId) => {
    try { await api.post(`/work/${workItemId}/timer/start`); } catch { /* the bar will say it didn't take */ }
    setPicking(false);
    window.dispatchEvent(new Event('pm-timer-changed'));
    fetchTimer();
  };

  // A slot pinned to a time today that has arrived — the one suggestion that is a decision
  // somebody already made, rather than anything the app merely thinks is a good idea. Nothing
  // starts itself off the back of "overdue" or "you were on this earlier".
  const plannedNow = ((idle && idle.suggestions) || []).find(s => s.why === 'planned for now') || null;
  // And whether now is a moment to be doing anything unasked: inside the working day, with the
  // practice's own nudging on, and not inside a "leave me alone for a bit".
  //
  // Deliberately not gated on `nudge_start`. That preference means "do not interrupt me about
  // starting a timer", and auto-start is the setting for somebody who would rather it just
  // happened — requiring both would make the sensible combination of the two do nothing.
  const autoOk = !!(prefs && prefs.auto_start)
    && !!(idle && idle.in_work_hours && (idle.work_hours || {}).nudge !== false)
    && Date.now() >= snoozedUntil;

  // The slot arriving and starting the timer itself, for anybody who has asked for that.
  //
  // The risk is logging time somebody is not working, and there are three answers to it: it is off
  // unless switched on, it says so and offers to undo, and the idle auto-stop that already exists
  // drops the time if nobody touches anything.
  useEffect(() => {
    if (timer || !autoOk || !plannedNow) return;
    const id = slotId(plannedNow);
    if (!id || autoStartedToday().includes(id)) return;
    rememberAutoStart(id);
    setAutoStarted(plannedNow);
    startOn(plannedNow.work_item_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, autoOk, plannedNow && plannedNow.work_item_id, plannedNow && plannedNow.plan_start]);

  // Started by itself, and stopped again by somebody who was not doing that. Discarded rather than
  // saved: the whole point of the undo is that the minutes are wrong.
  const undoAuto = async () => {
    setAutoStarted(null);
    try { await api.post('/timer/stop', { discard: true }); } catch { /* the bar will show what took */ }
    window.dispatchEvent(new Event('pm-timer-changed'));
    fetchTimer();
  };

  const snooze = () => {
    const until = snoozeUntilTomorrow();
    localStorage.setItem(SNOOZE_KEY, String(until));
    setSnoozedUntil(until);
    setPicking(false);
  };

  // Track real activity in the tab.
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const evts = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    evts.forEach(e => window.addEventListener(e, mark, { passive: true }));
    return () => evts.forEach(e => window.removeEventListener(e, mark));
  }, []);

  // Once per second: re-render (for the clock) and decide whether to prompt.
  useEffect(() => {
    if (!timer) { setPrompting(false); return; }
    const t = setInterval(() => {
      setTick(x => x + 1);
      // Somebody who has turned this off is never asked. Not asked more quietly, not asked later:
      // not asked — and if it is switched off while the question is up, the question goes.
      if (prefs && prefs.nudge_idle === false) { setPrompting(false); return; }
      // Paused is somebody saying they have stepped away. Asking whether they have stepped away
      // is the app not listening — and the answer it offers, dropping the idle minutes, is about
      // minutes that are already not being counted.
      if (timer.paused) { setPrompting(false); return; }
      if (Date.now() - timer.lastActiveMs > IDLE_LIMIT_MS) setPrompting(true);
    }, 1000);
    return () => clearInterval(t);
  }, [timer, prefs]);

  // Heartbeat: while active and the tab is visible, tell the server we're still here.
  useEffect(() => {
    if (!timer) return;
    const beat = async () => {
      const activeRecently = Date.now() - lastActivityRef.current < 65000;
      if (activeRecently && document.visibilityState === 'visible') {
        try { await api.post('/timer/heartbeat'); setTimer(t => (t ? { ...t, lastActiveMs: Date.now() } : t)); } catch { /* ignore */ }
      }
    };
    const hb = setInterval(beat, 60000);
    return () => clearInterval(hb);
  }, [timer]);

  // Nothing running. The bar stays — a timer you can only reach from one page is a timer that
  // spends the day switched off. It speaks up during working hours and sits quietly outside them.
  if (!timer) {
    // No prompt at all for somebody who has turned the bar off, for an automation login, or while a
    // screen has asked for quiet. Pay day still says itself if it is due — it is not about the timer.
    if ((prefs && prefs.timer_bar === false) || quietAsks > 0) {
      return <div className="timer-quiet-strip"><PayrollDueBar /></div>;
    }
    const quiet = Date.now() < snoozedUntil;
    const nudging = !!(idle && idle.in_work_hours && (idle.work_hours || {}).nudge !== false)
      && !quiet && !!(prefs && prefs.nudge_start !== false);
    const best = (idle && idle.suggestions && idle.suggestions[0]) || null;
    const rest = (idle && idle.suggestions ? idle.suggestions.slice(1) : []);
    return (
      <>
        <div className={`timer-bar idle${nudging ? ' nudging' : ''}`}>
          <span className="timer-bar-dot off" />
          {nudging && best ? (
            <>
              <span className="timer-bar-label">
                No timer running{best.why === 'planned for now' ? ' — you’re meant to be on' : ' — start on'}{' '}
                <strong>{best.next_task || best.title}</strong>{best.next_task ? ` · ${best.title}` : ''}{best.client_name ? ` · ${best.client_name}` : ''}
                {best.why && best.why !== 'planned for now' ? <span className="timer-bar-why"> ({best.why})</span> : null}?
              </span>
              <button className="timer-bar-start" onClick={() => startOn(best.work_item_id)}>▶ Start</button>
              {rest.length > 0 && <button className="timer-bar-alt" onClick={() => setPicking(true)}>Something else…</button>}
              <button className="timer-bar-alt" onClick={snooze} title="Quiet until tomorrow">Not now</button>
              <PayrollDueBar />
            </>
          ) : nudging ? (
            <>
              <span className="timer-bar-label">No timer running — nothing is planned for now.</span>
              <button className="timer-bar-alt" onClick={() => navigate('/planner')}>Open the Planner</button>
              <button className="timer-bar-alt" onClick={snooze} title="Quiet until tomorrow">Not now</button>
              <PayrollDueBar />
            </>
          ) : (
            <>
              <span className="timer-bar-label quiet">No timer running{quiet ? ' · quiet until tomorrow' : ''}</span>
              {/* Nothing planned used to mean no way to start a timer from here at all. On a page
                  that is itself the work, there is always something to start. */}
              {((idle && idle.suggestions && idle.suggestions.length > 0) || hereWork) && (
                <button className="timer-bar-start" onClick={() => setPicking(true)}>▶ Start a timer</button>
              )}
              {quiet && <button className="timer-bar-alt" onClick={() => { localStorage.removeItem(SNOOZE_KEY); setSnoozedUntil(0); }}>Ask me again</button>}
              <PayrollDueBar />
            </>
          )}
        </div>

        {picking && (
          <div className="modal-overlay" style={{ zIndex: 100 }} onClick={() => setPicking(false)}>
            <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
              <div className="modal-title">What are you working on?</div>
              <div className="timer-pick">
                {/* First, and said plainly. Somebody who opened this from Triage is almost
                    certainly about to time Triage, and making them find it in a list of
                    everything else is the app pretending not to know where they are. */}
                {hereWork && (
                  <button className="timer-pick-row here" onClick={() => startOn(hereWork.id)} autoFocus>
                    <span className="timer-pick-title">{here.label}</span>
                    <span className="timer-pick-why">the page you are on</span>
                  </button>
                )}
                {(idle && idle.suggestions ? idle.suggestions : []).map(sg => (
                  <button className="timer-pick-row" key={sg.work_item_id} onClick={() => startOn(sg.work_item_id)}>
                    <span className="timer-pick-title">{sg.title}</span>
                    <span className="timer-pick-why">
                      {sg.client_name ? `${sg.client_name} · ` : ''}{sg.why}{sg.plan_start ? ` · ${sg.plan_start}` : ''}
                    </span>
                  </button>
                ))}
                {(!idle || !idle.suggestions || idle.suggestions.length === 0) && !hereWork && (
                  <div className="timer-pick-none">Nothing open to time. Plan something in the Planner first.</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/planner')}>Open the Planner</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setPicking(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // What the timer has actually counted: wall clock since it started, less every finished pause,
  // and frozen where the current one began. The same arithmetic as `timerWorkedMs` on the server,
  // which is where the minutes are finally worked out — held together by a test, the way the two
  // date helpers are, because a banner that disagrees with what gets banked is worse than one
  // that does not count at all.
  const workedMs = workedMsOf(timer);
  const elapsed = fmtClock(workedMs / 1000);
  const idleMin = Math.round((Date.now() - timer.lastActiveMs) / 60000);

  // Stepping away without ending it. Optimistic, because the clock freezing the instant it is
  // pressed is the whole of what the button promises.
  //
  // It takes what it should end up as, not what to do to it. It used to take `resume`, so the
  // Pause button read `pause(true)` — which looks exactly like "pause: yes" and meant the
  // opposite. Both buttons were wired to it backwards and both did nothing: Pause asked the
  // server to resume a timer that was running, and Resume asked it to pause one already paused,
  // and the server is idempotent both ways, so each was a no-op. A name a call site can read
  // wrongly is a name that will be read wrongly.
  //
  // ── And the optimistic frame has to do the server's arithmetic, not half of it ──────────────────
  //
  // Raised in review: on resume this cleared `paused_at` and left `paused_ms` at the value it was
  // fetched with — which does not yet include the pause that is just ending. `workedMsOf` is
  // `(pausedAt || now) - started - paused_ms`, so with the mark gone and the total stale that is
  // plain wall clock since the start.
  //
  // Start at 9:00, pause at 9:10 having worked ten minutes, take a forty-minute call, press Resume
  // at 9:50: the banner read **50 minutes** until the round-trip returned, then snapped back to 10.
  // For a short pause it is imperceptible; for a lunch break the whole break appears on the clock.
  // And the request is wrapped in `catch { }`, so a resume that failed left the inflated reading up
  // until the next poll — with the server still holding the timer paused, which is "it says paused
  // and the number is climbing" from the other side. The likely shape of that report.
  //
  // Nothing wrong was ever banked: the server is right throughout, and it is the same sum it does
  // on the same event. Which is the point — one arithmetic, in both places, rather than an
  // optimistic frame that is its own idea of the truth.
  const setPaused = async (on) => {
    setTimer(t => (t ? {
      ...t,
      paused: on,
      paused_at: on ? new Date().toISOString().replace('T', ' ').slice(0, 19) : null,
      // Banked into the total in the same breath as the mark is cleared. Only on resume, and only
      // where there was a mark to measure from — resuming a timer that was not paused adds nothing.
      paused_ms: (Number(t.paused_ms) || 0) + (!on && t.paused_at ? Math.max(0, Date.now() - parseServerTime(t.paused_at)) : 0),
    } : t));
    if (!on) { lastActivityRef.current = Date.now(); setPrompting(false); }
    try { await api.post('/timer/pause', { resume: !on }); } catch { /* ignore */ }
    // Every other thing that happens to a timer says so; this one did not, so a pause pressed
    // here left the button on a task, and every other tab, counting a timer that had stopped.
    window.dispatchEvent(new Event('pm-timer-changed'));
    fetchTimer();
  };

  const stop = async (keepUntilMs) => {
    try {
      await api.post('/timer/stop', keepUntilMs ? { keep_until: new Date(keepUntilMs).toISOString() } : {});
    } catch { /* ignore */ }
    setPrompting(false);
    window.dispatchEvent(new Event('pm-timer-changed'));
  };

  // Point the running timer at a task (or clear it) from the banner, without banking.
  const setTimerTask = async (taskId) => {
    setTimer(t => (t ? { ...t, task_id: taskId ? Number(taskId) : null } : t)); // optimistic
    try { await api.post('/timer/task', { task_id: taskId || null }); } catch { /* ignore */ }
    fetchTimer();
  };

  // "I was working" — bump activity so we stop prompting and keep counting.
  const keepWorking = async () => {
    lastActivityRef.current = Date.now();
    try { await api.post('/timer/heartbeat'); } catch { /* ignore */ }
    setTimer(t => (t ? { ...t, lastActiveMs: Date.now() } : t));
    setPrompting(false);
  };

  return (
    <>
      <div className={`timer-bar${timer.paused ? ' paused' : ''}`}>
        <span className="timer-bar-dot" />
        <button
          type="button"
          className="timer-bar-label timer-bar-open"
          onClick={() => navigate(`/?focus=${timer.work_item_id}${timer.task_id ? `&task=${timer.task_id}` : ''}`)}
          title="Open the workflow you're timing"
        >
          {timer.paused ? 'Paused' : 'Timer running'} · <strong>{timer.title}</strong> ↗
        </button>
        <span className="timer-bar-task">
          {(timer.tasks && timer.tasks.length) ? (
            <select
              className="timer-bar-select"
              value={timer.task_id || ''}
              onChange={e => setTimerTask(e.target.value)}
              title="Which task within this workflow is this time for?"
            >
              <option value="">— whole workflow —</option>
              {timer.tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          ) : (timer.task_title ? <span className="timer-bar-sub">{timer.task_title}</span> : null)}
        </span>
        <span className={`timer-bar-time${timer.paused ? ' paused' : ''}`}>{elapsed}</span>
        {timer.paused
          ? <button className="timer-bar-resume" onClick={() => setPaused(false)} title="Pick it back up — the clock starts again from here">▶ Resume</button>
          : <button className="timer-bar-pause" onClick={() => setPaused(true)} title="Stepping away — the clock stops but the entry stays open">⏸ Pause</button>}
        <button className="timer-bar-stop" onClick={() => stop(null)}>⏹ Stop</button>
        <PayrollDueBar />
      </div>

      {/* It started itself, so it says so. A timer that appears out of nowhere is worse than no
          timer at all — the person it happens to stops trusting the figures rather than the
          feature. Dismissing it is the acknowledgement; undoing throws the minutes away, because
          the whole point of the undo is that they are wrong. */}
      {autoStarted && (
        <div className="timer-auto-strip">
          <span>
            Started automatically — <strong>{autoStarted.title}</strong> was planned for now.
          </span>
          <button className="timer-auto-undo" onClick={undoAuto}>Not working on that — stop and discard</button>
          <button className="timer-auto-ok" onClick={() => setAutoStarted(null)}>OK</button>
        </div>
      )}

      {/* The same question, in the bar rather than over the screen. Nothing is decided until
          somebody answers: the minutes are not dropped until they say they were away, and not
          kept until they say they were working. */}
      {prompting && (
        <div className="timer-idle-strip">
          <span className="timer-idle-dot" />
          <span>
            No activity for <strong>{idleMin} minutes</strong> — still on <strong>{timer.task_title || timer.title}</strong>?
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => stop(timer.lastActiveMs)}
            title="Stop the timer and keep only the time up to your last activity">I stepped away</button>
          <button className="btn btn-primary btn-sm" onClick={keepWorking}>Still working</button>
        </div>
      )}
    </>
  );
}

// Global search box (sidebar) — finds clients, work items and tasks; clicking a result
// jumps to the relevant page (work/tasks open the Work page focused on that card).
function GlobalSearch({ onNavigate }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get('/search', { params: { q } }); setRes(r.data); setOpen(true); } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = (path) => { setQ(''); setRes(null); setOpen(false); onNavigate && onNavigate(); navigate(path); };
  const total = res ? res.clients.length + res.work.length + res.tasks.length : 0;

  return (
    <div className="sidebar-search" ref={ref}>
      <span className="sidebar-search-icon">🔍</span>
      <input
        className="sidebar-search-input"
        placeholder="Search…"
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => { if (res) setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
      />
      {open && res && (
        <div className="search-results">
          {total === 0 && <div className="search-empty">No matches for “{q}”</div>}
          {res.clients.length > 0 && <div className="search-group-label">Clients</div>}
          {res.clients.map(c => (
            <button key={'c' + c.id} className="search-item" onClick={() => go(`/clients?focus=${c.id}`)}>
              <span className="search-item-icon">🏢</span><span className="search-item-text">{c.name}</span>
            </button>
          ))}
          {res.work.length > 0 && <div className="search-group-label">Work items</div>}
          {res.work.map(w => (
            <button key={'w' + w.id} className="search-item" onClick={() => go(`/?focus=${w.id}`)}>
              <span className="search-item-icon"><Icon name="clipboard" size={14} /></span>
              <span className="search-item-text">{w.title}{w.client_name && <span className="search-item-sub"> · {w.client_name}</span>}</span>
            </button>
          ))}
          {res.tasks.length > 0 && <div className="search-group-label">Tasks</div>}
          {res.tasks.map(t => (
            <button key={'t' + t.id} className="search-item" onClick={() => go(`/?focus=${t.work_item_id}`)}>
              <span className="search-item-icon">{t.is_complete ? '✅' : '☐'}</span>
              <span className="search-item-text">{t.title}<span className="search-item-sub"> · {t.work_title}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MainLayout() {
  // In a meeting with a client (MeetingMode.js): only that client's pages, nothing around them.
  const { meeting } = useMeeting();
  const [emailingBackup, setEmailingBackup] = useState(false);
  const [backupMsg, setBackupMsg] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('pm_sidebar_collapsed') === '1');
  // The guided tour: shown once, on a new starter's first sign-in, and on request after that.
  const [tourOpen, setTourOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  // How much is signed off by its preparer and waiting on a reviewer. On the sidebar, because a
  // review queue nobody is reminded of is a review queue that grows.
  const [reviewCount, setReviewCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);

  const toggleCollapse = () => setCollapsed(c => {
    const n = !c;
    localStorage.setItem('pm_sidebar_collapsed', n ? '1' : '0');
    return n;
  });

  const loadMe = useCallback(() => {
    api.get('/me').then(r => { setMe(r.data); if (r.data && r.data.portal_url) setPortalLinkBase(r.data.portal_url); }).catch(() => {});
  }, []);
  useEffect(() => { loadMe(); }, [loadMe]);
  // Changing a preference on the Settings page has to reach the timer bar, which is a different
  // part of the screen and would otherwise go on nudging until the next reload.
  useEffect(() => {
    window.addEventListener('pm-me-changed', loadMe);
    return () => window.removeEventListener('pm-me-changed', loadMe);
  }, [loadMe]);

  // A new starter gets shown round on their first sign-in. It waits for the sidebar to be there
  // to point at, and never interrupts someone who has already been through it.
  useEffect(() => {
    if (!me || me.tour_done || tourOpen) return;
    const t = setTimeout(() => setTourOpen(true), 600);
    return () => clearTimeout(t);
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  // Running it again — from the sidebar, whenever they like.
  const replayTour = async () => {
    try { await api.post('/me/tour-reset'); } catch { /* it can still run in this session */ }
    setMenuOpen(false);
    setTourOpen(true);
  };

  // Owner-only: count of pending bank-account requests, for the Clients nav badge.
  useEffect(() => {
    if (!me?.owner) return;
    const refresh = () => api.get('/bank-accounts/pending-count').then(r => setPendingApprovals(r.data.count || 0)).catch(() => {});
    refresh();
    window.addEventListener('pm-approvals-changed', refresh);
    return () => window.removeEventListener('pm-approvals-changed', refresh);
  }, [me]);

  // Suggestions nobody has answered yet. The one pending queue with nothing on the nav to say so,
  // which is how it came to be sitting unchecked — so it gets the same badge as the rest.
  useEffect(() => {
    const refresh = () => api.get('/feedback/count').then(r => setFeedbackCount(r.data.count || 0)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 120000);
    window.addEventListener('pm-feedback-changed', refresh);
    return () => { clearInterval(iv); window.removeEventListener('pm-feedback-changed', refresh); };
  }, []);

  // Waiting to be reviewed, for the badge. Refreshed on the same events that change it.
  useEffect(() => {
    const refresh = () => api.get('/review-queue/count').then(r => setReviewCount(r.data.count || 0)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 120000);
    window.addEventListener('pm-reviews-changed', refresh);
    window.addEventListener('pm-work-changed', refresh);
    return () => { clearInterval(iv); window.removeEventListener('pm-reviews-changed', refresh); window.removeEventListener('pm-work-changed', refresh); };
  }, []);

  // People to message today, for the Sales badge. The whole point of the section is that it tells
  // you there is something to do rather than waiting to be visited.
  // Only for somebody with Sales — asked of anyone else it is a 403 every five minutes.
  const canSales = holds(me, 'can_use_sales');
  useEffect(() => {
    if (!canSales) return undefined;
    const refresh = () => api.get('/crm/queue/count').then(r => setSalesCount(r.data.count || 0)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 300000);
    window.addEventListener('pm-sales-changed', refresh);
    return () => { clearInterval(iv); window.removeEventListener('pm-sales-changed', refresh); };
  }, [canSales]);

  // Unread in-app notifications, for the Triage nav badge.
  // Only for somebody with Triage — the count route is behind it.
  const canTriage = holds(me, 'can_use_triage');
  useEffect(() => {
    if (!canTriage) return undefined;
    const refresh = () => api.get('/notifications/count').then(r => setNotifCount(r.data.count || 0)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 60000);
    window.addEventListener('pm-notifs-changed', refresh);
    return () => { clearInterval(iv); window.removeEventListener('pm-notifs-changed', refresh); };
  }, [canTriage]);

  const logout = () => {
    localStorage.removeItem('pm_token');
    window.dispatchEvent(new Event('pm-unauthorized'));
  };

  const emailBackup = async () => {
    setEmailingBackup(true);
    setBackupMsg('');
    try {
      const res = await api.post('/backup/email');
      const to = res.data.to || 'your email';
      setBackupMsg(res.data.attached === false
        ? `Backup is too large to attach — emailed a notice to ${to}.`
        : `✓ Backup emailed to ${to}.`);
      setTimeout(() => setBackupMsg(''), 8000);
    } catch (e) {
      setBackupMsg(e.response?.data?.error || 'Could not email backup — is email set up?');
    } finally {
      setEmailingBackup(false);
    }
  };

  const canViewReports = !!(me?.owner || me?.perms?.can_view_reports);
  const canManageWork = !!(me?.owner || me?.perms?.can_manage_work);
  // Section access. Undefined while `me` is still loading, so treat that as yes and let the page
  // settle rather than flashing a refusal at the owner.
  const section = (key) => !me || !!me.owner || !!(me.perms && me.perms[key]);

  return (
    <MeContext.Provider value={me}>
    {/* What changed since the owner last looked, read out of the Done log. Inside the provider,
        because it has to know who is looking — owner only for now, since it is the first thing
        anybody sees after a deploy and a popup that lands wrong should land on one person first. */}
    {!meeting && <WhatsNew />}
    <RecorderProvider>
    <div className={`app-layout ${collapsed ? 'sidebar-collapsed' : ''}${meeting ? ' meeting-mode' : ''}`}>
      {/* Mobile top bar (hidden on desktop) */}
      {!meeting && <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">☰</button>
        <div className="mobile-brand"><BrandMark size={22} /> The Hub</div>
      </div>}

      {menuOpen && !meeting && <div className="sidebar-overlay" onClick={() => setMenuOpen(false)} />}

      {!meeting && <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <BrandMark size={28} />
          <span className="brand-name">The Hub<span className="brand-sub">by ZG Books</span></span>
        </div>
        {!collapsed && <GlobalSearch onNavigate={() => setMenuOpen(false)} />}
        <nav className="sidebar-nav" onClick={() => setMenuOpen(false)}>
          <div className="nav-section-label">Work</div>
          <NavLink to="/my-tasks" title="My Tasks" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            <span className="nav-label">My Tasks</span>
            {reviewCount > 0 && <span className="nav-badge" title={`${reviewCount} waiting for your review`}>{reviewCount}</span>}
          </NavLink>
          <NavLink to="/" end title="Work" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            <span className="nav-label">Work</span>
          </NavLink>
{section('can_use_triage') && <NavLink to="/triage" title="Triage" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            <span className="nav-label">Triage</span>
            {notifCount > 0 && <span className="nav-badge" title={`${notifCount} unread notification${notifCount===1?'':'s'}`}>{notifCount}</span>}
          </NavLink>}
          <NavLink to="/planner" title="Planner" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span className="nav-label">Planner</span>
          </NavLink>
          {section('can_use_assistant') && <NavLink to="/assistant" title="Ask the Hub" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><circle cx="9" cy="10" r="1"/><circle cx="12" cy="10" r="1"/><circle cx="15" cy="10" r="1"/></svg>
            <span className="nav-label">Ask the Hub</span>
          </NavLink>}
          <NavLink to="/requests" title="Requests" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
            <span className="nav-label">Requests</span>
          </NavLink>
          {section('can_view_payroll') && <NavLink to="/payroll" title="Payroll" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></svg>
            <span className="nav-label">Payroll</span>
          </NavLink>}
          {section('can_view_uncoded') && <NavLink to="/reconciliation" title="Uncoded Transactions" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3v18h18"/><path d="M7 14l3-3 3 3 5-5"/><polyline points="20 9 20 5 16 5" /></svg>
            <span className="nav-label">Uncoded</span>
          </NavLink>}
          {canViewReports && (
            <NavLink to="/time" title="Time" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
              <span className="nav-label">Time</span>
            </NavLink>
          )}
          {/* Grouped: what is about clients, what runs the practice, and administration. It was one
              "Management" list of thirteen links in no particular order. */}
          <div className="nav-section-label">Clients</div>
          <NavLink to="/clients" title="Clients" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span className="nav-label">Clients</span>
            {pendingApprovals > 0 && <span className="nav-badge" title={`${pendingApprovals} bank account${pendingApprovals===1?'':'s'} awaiting approval`}>{pendingApprovals}</span>}
          </NavLink>
          {can(me, 'can_manage_clients') && (
            <NavLink to="/intake" title="Client details forms" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z"/><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/><path d="M9 12h6M9 16h4"/></svg>
              <span className="nav-label">Client forms</span>
            </NavLink>
          )}
          {/* Every client's BAS or IAS for one period. Lives under Management rather than beside
              the work itself: the question it answers is about the practice — who has not lodged
              — and not about any one workflow. */}
          {canManageWork && (
            <NavLink to="/activity-statements" title="Activity statements" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 2v4M16 2v4"/><path d="M9 14l2 2 4-4"/></svg>
              <span className="nav-label">Activity statements</span>
            </NavLink>
          )}
          {section('can_use_sales') && (
            <NavLink to="/sales" title="People to message" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>
              <span className="nav-label">Sales</span>
              {salesCount > 0 && <span className="nav-badge" title={`${salesCount} to message or follow up today`}>{salesCount}</span>}
            </NavLink>
          )}
          {me?.owner && (
            <NavLink to="/quotes" title="Quotes" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
              <span className="nav-label">Quotes</span>
            </NavLink>
          )}
          <div className="nav-section-label">Practice</div>
          {canManageWork && (
            <NavLink to="/templates" title="Templates" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              <span className="nav-label">Templates</span>
            </NavLink>
          )}
          {me?.owner && (
            <NavLink to="/recordings" title="Recordings" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
              <span className="nav-label">Recordings</span>
            </NavLink>
          )}
          {me?.owner && (
            <NavLink to="/pricing-admin" title="Pricing" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              <span className="nav-label">Pricing</span>
            </NavLink>
          )}
          {me?.owner && (
            <NavLink to="/import" title="Import" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              <span className="nav-label">Import</span>
            </NavLink>
          )}
          <NavLink to="/performance" title="How it is going" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3v18h18"/><path d="M7 15l4-5 3 3 5-7"/></svg>
            <span className="nav-label">How it is going</span>
          </NavLink>
          <div className="nav-section-label">Admin</div>
          {me?.owner && (
            <NavLink to="/staff" title="Staff" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11l-2 2-1-1"/></svg>
              <span className="nav-label">Staff</span>
            </NavLink>
          )}
          {/* Everybody's, now that there is something on it for everybody: what the timer is
              allowed to say to you. The practice-wide settings below it are still the owner's. */}
            <NavLink to="/settings" title="Settings" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              <span className="nav-label">Settings</span>
            </NavLink>
          {/* For everybody, not only the owner — a suggestion box the people with the suggestions
              cannot reach is a suggestion box for one person. */}
          <NavLink to="/feedback" title="Ideas & problems" className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>
            <span className="nav-label">Ideas &amp; problems</span>
            {feedbackCount > 0 && <span className="nav-badge" title={`${feedbackCount} suggestion${feedbackCount === 1 ? '' : 's'} nobody has answered yet`}>{feedbackCount}</span>}
          </NavLink>
        </nav>
        <button className="sidebar-collapse-toggle" onClick={toggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <span className="collapse-chevron">{collapsed ? '»' : '«'}</span>
          <span className="nav-label">Collapse</span>
        </button>
        <div className="sidebar-footer">
          {me && <div style={{ fontSize: '12px', color: 'var(--text)', marginBottom: '10px' }}><Icon name="user" /> {me.name}{me.owner ? ' · owner' : ''}</div>}
          {/* The popup arrives on its own after a deploy. This is the way back into it — to
              read something again, or to put a picture on an entry that was already read. */}
          {me?.owner && (
            <button className="sidebar-backup-btn" onClick={openWhatsNew}>✨ What’s new</button>
          )}
          {me?.owner && (
            <>
              <button className="sidebar-backup-btn" onClick={emailBackup} disabled={emailingBackup}>
                {emailingBackup ? 'Sending…' : <><Icon name="mail" /> Email me a backup</>}
              </button>
              {backupMsg && <div style={{ fontSize: '11px', color: 'var(--green)', marginTop: '6px' }}>{backupMsg}</div>}
            </>
          )}
          <button className="sidebar-backup-btn" onClick={replayTour} style={{ marginTop: '8px' }}>🧭 Show me round</button>
          <button className="sidebar-backup-btn" onClick={logout} style={{ marginTop: '8px' }}>↩ Log out</button>
          <div style={{ marginTop: '10px' }}>v1.0 · Stage 1</div>
        </div>
      </aside>}
      <main className="main-content">
        <MeetingBar />
        {/* Above the timer bar: what has to happen today comes before what is being counted.
            None of the three in a meeting: each can name another client. */}
        {/* One strip: the timer, with pay day as a chip on its right (PayrollDueBar, inside). */}
        {!meeting && <TimerBar />}
        {!meeting && <StartPrompt />}
        <MeetingGuard>
        <React.Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Work />} />
          <Route path="/planner" element={<Planner />} />
          <Route path="/assistant" element={<Gated ok={section('can_use_assistant')} what="Ask the Hub"><Assistant /></Gated>} />
          <Route path="/triage" element={<Gated ok={section('can_use_triage')} what="Triage"><Triage /></Gated>} />
          <Route path="/payroll" element={<Gated ok={section('can_view_payroll')} what="Payroll"><Payroll /></Gated>} />
          <Route path="/my-tasks" element={<MyTasks />} />
          <Route path="/requests" element={<Requests />} />
          <Route path="/reconciliation" element={<Gated ok={section('can_view_uncoded')} what="Uncoded transactions"><Reconciliation /></Gated>} />
          <Route path="/time" element={<TimePage />} />
          <Route path="/staff" element={<Staff />} />
          {/* Review is a tab on My Tasks now. */}
          <Route path="/review" element={<Navigate to="/my-tasks?tab=review" replace />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/activity-statements" element={<Gated ok={canManageWork} what="Activity statements"><ActivityStatements /></Gated>} />
          {/* Where the phone's share sheet lands. Inside the signed-in app on purpose: a file
              shared from WhatsApp is somebody's client's paperwork, and it is filed by whoever is
              already logged in here rather than by whoever has the link. */}
          <Route path="/share" element={<Shared />} />
          {/* Not gated: anybody signed in can say what they think should change. The route hands
              back only your own unless you are the owner, so there is nothing here to keep
              anybody out of. */}
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/sales" element={<Gated ok={section('can_use_sales')} what="Sales"><Sales /></Gated>} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/pricing-admin" element={<PricingAdmin />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/recordings" element={<Recordings />} />
          <Route path="/intake" element={<IntakeForms />} />
          <Route path="/estimator-admin" element={<EstimatorAdmin />} />
          <Route path="/import" element={<Import />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
        </Routes>
        </React.Suspense>
        </MeetingGuard>
      </main>
      {tourOpen && !meeting && <Tour onClose={() => { setTourOpen(false); setMe(m => (m ? { ...m, tour_done: true } : m)); }} />}
    </div>
    </RecorderProvider>
    </MeContext.Provider>
  );
}

// A section someone hasn't been given. Deep-linking to it says so plainly, rather than rendering
// a page whose every call comes back refused.
// On the portal subdomain, brand the tab as the client portal.
if (isPortalHost) { try { document.title = 'ZG Books — Client Portal'; } catch { /* noop */ } }

// Polls the backend for the deployed frontend version; if it changes while the app is open (a new
// version has shipped), shows a ribbon prompting a refresh to load it.
function UpdateRibbon() {
  const baseline = useRef(null);
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const v = (await api.get('/version')).data.version;
        if (!alive || !v) return;
        if (baseline.current == null) baseline.current = v;
        else if (v !== baseline.current) setStale(true);
      } catch (e) { /* ignore network blips */ }
    };
    check();
    // A page that could not load because it changed underneath us is a new version too.
    const onNew = () => setStale(true);
    window.addEventListener('pm-new-version', onNew);
    const iv = setInterval(check, 3 * 60 * 1000);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('focus', onFocus); window.removeEventListener('pm-new-version', onNew); };
  }, []);
  if (!stale || dismissed) return null;
  // Small and in the corner: a new version is worth knowing about, not worth covering the page for.
  return (
    <div className="update-ribbon update-toast" role="status">
      <span>✨ New version — refresh when ready.</span>
      <span className="update-ribbon-actions">
        <button className="update-ribbon-refresh" onClick={() => window.location.reload()}>Refresh</button>
        <button className="update-ribbon-dismiss" title="Dismiss" onClick={() => setDismissed(true)}>×</button>
      </span>
    </div>
  );
}

// Shows a calm "updating" ribbon while the backend is briefly unreachable (a Render deploy is
// restarting it). Requests auto-retry in the background (see api.js), so the current screen stays
// put and finishes once the update lands — no error, no lost work. Debounced so a one-off blip
// doesn't flash it.
function ConnectionRibbon() {
  const [down, setDown] = useState(false);
  // Waiting out a rate limit is not a deploy. The request is retried either way — that part was
  // right — but the ribbon said "The Hub is upgrading" for both, which is a false statement about
  // somebody else's server being busy, on a screen whose whole purpose is to say what happened.
  const [busy, setBusy] = useState(null);   // { seconds } while Xero is rate limiting us
  const timer = useRef(null);
  useEffect(() => {
    const goDown = () => { if (!timer.current && !down) timer.current = setTimeout(() => { timer.current = null; setDown(true); }, 2500); };
    const goUp = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } setDown(false); setBusy(null); };
    const goBusy = (e) => setBusy({ seconds: (e && e.detail && Number(e.detail.seconds)) || 0 });
    window.addEventListener('pm-backend-offline', goDown);
    window.addEventListener('pm-backend-busy', goBusy);
    window.addEventListener('pm-backend-online', goUp);
    return () => {
      window.removeEventListener('pm-backend-offline', goDown);
      window.removeEventListener('pm-backend-busy', goBusy);
      window.removeEventListener('pm-backend-online', goUp);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [down]);
  // A successful request clears this, which is the ordinary end of it. But if the retries run out
  // the error surfaces on the screen that asked and nothing says "online", so it needs its own
  // way out — a ribbon that cannot clear itself is the thing it was put here to avoid.
  useEffect(() => {
    if (!busy) return undefined;
    const t = setTimeout(() => setBusy(null), Math.max(5000, (busy.seconds || 0) * 1000 + 5000));
    return () => clearTimeout(t);
  }, [busy]);
  // While showing the ribbon, actively probe the backend so it always clears once the deploy is
  // done — even if nothing else happens to be retrying at that moment.
  useEffect(() => {
    if (!down) return undefined;
    const iv = setInterval(() => {
      api.get('/version', { __noRetry: true }).then(() => setDown(false)).catch(() => {});
    }, 4000);
    return () => clearInterval(iv);
  }, [down]);
  // The rate limit wins the ribbon when both are somehow true: it is the more specific answer,
  // and it is the one the user can do nothing about but wait for.
  if (busy) {
    return (
      <div className="update-ribbon updating-ribbon">
        <span className="updating-spinner" aria-hidden="true" />
        <span>
          <Icon name="clock" /> Xero is rate limiting us{busy.seconds > 0 ? ` for about ${busy.seconds} second${busy.seconds === 1 ? '' : 's'}` : ''} — waiting, then trying again. Nothing is lost.
        </span>
      </div>
    );
  }
  if (!down) return null;
  return (
    <div className="update-ribbon updating-ribbon">
      <span className="updating-spinner" aria-hidden="true" />
      <span>✨ The Hub is upgrading with new features — reconnecting you now. This will only take a moment.</span>
    </div>
  );
}

export default function App() {
  return (
    <>
    <UpdateRibbon />
    <ConnectionRibbon />
    {/* One of these for the whole app: a question is asked from an event handler anywhere and
        answered here. Outside the router, because a question can outlive a navigation. */}
    <ConfirmHost />
    {/* One throw in one component used to unmount everything — header, sidebar, every page — and
        leave a white screen with nothing on it to report. Inside the router, so the boundary
        outlives what it catches and its two buttons still have somewhere to go. */}
    <BrowserRouter>
      <ErrorBoundary>
      <React.Suspense fallback={<PageLoading portal={isPortalHost || /^\/(portal|client|c|m|preview|form|quote|book|pricing|estimator)(\/|$)/.test(window.location.pathname)} />}>
      <Routes>
        {/* Public, sidebar-free client portal (login-gated) */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/quote/:token" element={<QuoteView />} />
        <Route path="/estimator" element={<Estimator />} />
        {/* The client details form. No login: the token in the link is the whole of it, which is
            the point — it goes to people who are not clients yet. */}
        <Route path="/form/:token" element={<IntakeForm />} />
        <Route path="/portal" element={<ClientPortalAll />} />
        <Route path="/portal/task/:taskId" element={<ClientPortalAll />} />
        <Route path="/portal/set-password" element={<SetPassword />} />
        <Route path="/set-password" element={<StaffSetPassword />} />
        {/* Per-workflow share link (still token-based) */}
        <Route path="/client/:token" element={<ClientPortal />} />
        <Route path="/client/:token/task/:taskId" element={<ClientPortal />} />
        {/* Old magic links now lead to the login */}
        <Route path="/c/:token" element={<MagicLink />} />
        <Route path="/c/:token/task/:taskId" element={<MagicLink />} />
        {/* A client email's one-click link: signed in, landed where the email is about. */}
        <Route path="/m/:token" element={<EmailLink />} />
        <Route path="/m/:token/task/:taskId" element={<EmailLink />} />
        {/* Public Calendly-style booking page */}
        <Route path="/book" element={<BookingPage />} />
        {/* A meeting type per link: /book/intro is fifteen minutes, /book/deep-dive an hour. The
            bare /book still works and means the first type, because that link is already out in
            the world in signatures and old emails. */}
        <Route path="/book/:slug" element={<BookingPage />} />
        {/* Staff read-only preview of a client's portal */}
        <Route path="/preview/:token" element={<PreviewEntry />} />
        <Route path="/preview/:token/task/:taskId" element={<PreviewEntry />} />
        {/* On the portal subdomain there is no staff app — everything else goes to the portal. */}
        <Route path="/*" element={isPortalHost ? <Navigate to="/portal" replace /> : <AuthGate><MeetingProvider><MainLayout /></MeetingProvider></AuthGate>} />
      </Routes>
      </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
    </>
  );
}
